import { useMemo } from 'react';

import { PROMPT_MENU_TYPE } from 'src/components/RichSlate';

import { ACTION } from '../../const';
import {
  createDocumentResourceItem,
  createImageResourceItem,
  createNewUrlItem,
  createUrlResourceItem,
} from '../../utils';
import { PromptMenuItem } from '../PromptMenuItem';

export const useMenus = ({
  menuType,
  searchText,
  filteredBankItems,
  filteredUrlResources,
  filteredDocumentResources,
  filteredImageResources,
  noTags,
  isApplicationModeInfinity,
  quickSearchMode,
  onAction,
}) => {
  const messagingBankMenu = useMemo(
    () =>
      filteredBankItems?.map((item, index) => (
        <PromptMenuItem
          type={PROMPT_MENU_TYPE.bank}
          item={item}
          index={index}
          menuType={menuType}
          key={item.id}
          isApplicationModeInfinity={isApplicationModeInfinity}
          quickSearchMode={quickSearchMode}
          onAction={onAction}
        />
      )),
    [filteredBankItems, menuType, isApplicationModeInfinity, quickSearchMode, onAction]
  );

  const documentItems = useMemo(
    () => filteredDocumentResources?.map(createDocumentResourceItem),
    [filteredDocumentResources]
  );

  const documentMenu = useMemo(
    () =>
      (documentItems || []).map((item) => (
        <PromptMenuItem
          type={item.type}
          item={item}
          noTags={noTags}
          key={item.id}
          quickSearchMode={quickSearchMode}
          menuType={menuType}
          onAction={onAction}
        />
      )),
    [noTags, documentItems, quickSearchMode, menuType, onAction]
  );

  const imageItems = useMemo(
    () => filteredImageResources?.map(createImageResourceItem),
    [filteredImageResources]
  );

  const imagesMenu = useMemo(
    () =>
      (imageItems || []).map((item) => (
        <PromptMenuItem
          type={item.type}
          item={item}
          noTags={noTags}
          key={item.id}
          quickSearchMode={quickSearchMode}
          menuType={menuType}
          onAction={onAction}
        />
      )),
    [noTags, imageItems, quickSearchMode, menuType, onAction]
  );

  const urlItems = useMemo(() => {
    if (filteredUrlResources?.length > 0) {
      return filteredUrlResources?.map(createUrlResourceItem);
    }

    if (searchText?.length > 0) {
      const newItem = createNewUrlItem(searchText, () =>
        onAction(ACTION.insertUrl, { url: searchText, noTags })
      );
      if (newItem) {
        return [newItem];
      }
    }

    return [];
  }, [filteredUrlResources, searchText, onAction, noTags]);

  const urlsMenu = useMemo(
    () =>
      (urlItems || []).map((item) => (
        <PromptMenuItem
          type={item.type}
          item={item}
          noTags={noTags}
          key={item.id}
          quickSearchMode={quickSearchMode}
          menuType={menuType}
          onAction={onAction}
        />
      )),
    [urlItems, noTags, quickSearchMode, menuType, onAction]
  );

  return {
    messagingBankMenu,
    documentMenu,
    imagesMenu,
    urlsMenu,
    urlItems,
    documentItems,
    imageItems,
  };
};
